import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

export default function PaymentResult({ success }) {
  const [params] = useSearchParams();
  const api = useApi();
  const qc = useQueryClient();
  const [state, setState] = useState(success ? "checking" : "cancelled");

  useEffect(() => {
    if (!success) return;
    const sid = params.get("session_id");
    if (!sid) return setState("error");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await qc.invalidateQueries({ queryKey: ["me"] });
          return setState("paid");
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch (e) { /* retry */ }
      if (++tries >= 15) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [success, params, api, qc]);

  const view = {
    checking: { icon: <Loader2 className="animate-spin text-blue-600" size={40} />, title: "Confirming your payment...", body: "This only takes a few seconds." },
    paid: { icon: <CheckCircle2 className="text-green-600" size={40} />, title: "Welcome to Pro!", body: "You now have unlimited exports. Your extension picks this up automatically." },
    timeout: { icon: <Loader2 className="text-amber-500" size={40} />, title: "Still processing", body: "Your payment is being confirmed. Refresh your dashboard in a minute." },
    error: { icon: <XCircle className="text-red-500" size={40} />, title: "Payment not completed", body: "No charge was made. You can try again from the pricing page." },
    cancelled: { icon: <XCircle className="text-slate-400" size={40} />, title: "Checkout cancelled", body: "No charge was made. You can upgrade anytime." },
  }[state];

  return (
    <main className="max-w-lg mx-auto px-4 py-20 text-center space-y-4" data-testid={`payment-${state}`}>
      <div className="flex justify-center">{view.icon}</div>
      <h1 className="font-heading text-3xl font-bold">{view.title}</h1>
      <p className="text-slate-600">{view.body}</p>
      <div className="flex justify-center gap-3 pt-4">
        <Link to="/" data-testid="payment-dashboard-link" className="h-11 px-5 rounded-md bg-[#1E293B] text-white text-sm font-semibold hover:bg-blue-600 flex items-center">Go to dashboard</Link>
        {state !== "paid" && <Link to="/pricing" className="h-11 px-5 rounded-md border border-slate-300 text-sm font-semibold flex items-center">Pricing</Link>}
      </div>
    </main>
  );
}
